import { useLayoutEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import PulseLoader from "react-spinners/PulseLoader";

import fileNumberIcon from "@/assets/filenumber.svg";

import birthdayIcon from "../../assets/images/birthdayicon.svg";
import incidentIcon from "../../assets/images/incident.svg";
import {
  calculateAge,
  formatCalendarDate,
  formatTimestampDate,
  formatPhoneNumber,
  getCaseId,
  getClientId,
} from "../../Utils/helper";

import avatarImage from "./../../assets/images/avatar.svg";
import "./LastAccessedCasesModal.css";

const CARD_WIDTH = 320;
const CARD_HEIGHT = 94 + 30 + 30;
const MAX_COLUMNS = 4;
const MAX_ROWS = 5;
const MAX_CASES = MAX_COLUMNS * MAX_ROWS;
const calculateGridLayout = () => {
  const cardWidth = Math.min(CARD_WIDTH, Math.max(1, window.innerWidth - 48));
  const columns = Math.min(
    MAX_COLUMNS,
    Math.max(1, Math.floor((window.innerWidth - 48) / cardWidth))
  );
  const rows = Math.min(
    MAX_ROWS,
    Math.max(1, Math.floor((window.innerHeight - 125) / CARD_HEIGHT))
  );
  return { columns, rows, cardWidth, modalWidth: columns * cardWidth, totalSlots: columns * rows };
};

const LastAccessedCasesModal = ({
  isOpen,
  onClose,
  lastAccessedCases,
  extractDigits,
  onCaseClick,
  isLoading,
}) => {
  const [gridLayout, setGridLayout] = useState(calculateGridLayout);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const updateLayout = () => setGridLayout(calculateGridLayout());
    updateLayout();
    window.addEventListener("resize", updateLayout);
    return () => window.removeEventListener("resize", updateLayout);
  }, [isOpen]);

  const { columns, rows, cardWidth, totalSlots, modalWidth } = gridLayout;

  const currentCaseId = getCaseId();
  const currentClientId = getClientId();
  const casesByClient = (lastAccessedCases || []).flatMap((caseData) => {
    const clients = Array.isArray(caseData?.for_client)
      ? caseData.for_client
      : [caseData?.for_client];

    return clients.filter(Boolean).map((client) => ({
      ...caseData,
      for_client: client,
    }));
  });
  const sortedCases = casesByClient.slice(0, MAX_CASES).sort((a, b) => {
    const aIsCurrent =
      String(a.id) === String(currentCaseId) && String(a.for_client.id) === String(currentClientId);
    const bIsCurrent =
      String(b.id) === String(currentCaseId) && String(b.for_client.id) === String(currentClientId);

    if (aIsCurrent) return -1;
    if (bIsCurrent) return 1;
    return 0;
  });

  // only show as many cases as the grid can hold; pad the rest with
  // empty checkerboard cells
  const visibleCases = sortedCases.slice(0, totalSlots || 0);
  const filledSlots = visibleCases.length;
  const emptySlots = Math.max((totalSlots || 0) - filledSlots, 0);
  return (
    <>
      <Modal
        show={isOpen}
        onHide={onClose}
        dialogClassName="last-access-modal-classname-dialog"
        contentClassName="last-access-modal-content-classname"
        style={{ "--last-access-modal-width": `${modalWidth}px` }}
      >
        <Modal.Header className="tfbp-modal-header">
          <Modal.Title className="tfbp-modal-title">Previously Accessed Cases</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div
            className="gap-0"
            style={{
              display: "grid",
              width: `${modalWidth}px`,
              height: `${rows * CARD_HEIGHT}px`,
              gridTemplateColumns: `repeat(${columns}, ${cardWidth}px)`,
              gridTemplateRows: `repeat(${rows}, ${CARD_HEIGHT}px)`,
              maxHeight: "calc(100vh - 125px)", // 50 top + 50 bottom + 25 header
              minHeight: "0",
              overflow: "hidden", // content is truncated to fit, never scrolls
            }}
          >
            {isLoading && (
              <PulseLoader
                loading={isLoading}
                size={16}
                aria-label="Loading Spinner"
                data-testid="loader"
                color="#19395F"
                style={{
                  gridColumn: "1 / -1",
                  gridRow: "1 / -1",
                  height: "100%",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              />
            )}

            {!isLoading &&
              visibleCases.map((caseData, index) => {
                const rowIndex = Math.floor(index / columns);
                const colIndex = index % columns;
                const isEven = (rowIndex + colIndex) % 2 === 0;
                const isCurrentCase =
                  String(caseData.id) === String(currentCaseId) &&
                  String(caseData.for_client.id) === String(currentClientId);
                const caseHref = `/bp-case/${caseData.for_client.id}/${caseData.id}`;
                const clientName = [caseData.for_client.first_name, caseData.for_client.last_name]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <div
                    key={`${caseData.id}-${caseData.for_client.id}`}
                    className={`last-access-case-card ${isEven ? "bg-checkerboard-primary" : "bg-checkerboard-secondary"}`}
                    style={{ height: `${CARD_HEIGHT}px` }}
                  >
                    <button
                      type="button"
                      className="last-access-case-action"
                      aria-label={`Click to Load Case: ${clientName}`}
                      disabled={isCurrentCase}
                      onClick={isCurrentCase ? undefined : (e) => onCaseClick(e, caseData)}
                    >
                      Click to Load Case
                    </button>
                    <a
                      href={isCurrentCase ? undefined : caseHref}
                      className="last-access-case-panel"
                      aria-label={`Load case for ${clientName}`}
                      aria-disabled={isCurrentCase || undefined}
                      tabIndex={isCurrentCase ? -1 : undefined}
                      onClick={isCurrentCase ? undefined : (e) => onCaseClick(e, caseData)}
                    >
                      {/* Row 1 - Client Info */}
                      <div
                        className="d-flex align-items-center justify-content-center p-r-5 p-l-5"
                        style={{ height: "21px" }}
                      >
                        <div
                          className="header-client-image"
                          style={{ marginLeft: "0px", marginRight: "5px" }}
                        >
                          {caseData?.for_client.profile_pic_29p ? (
                            <div
                              className="image-container-modal d-flex align-items-center justify-content-center"
                              style={{
                                width: "19px",
                                height: "19px",
                              }}
                            >
                              <img
                                src={caseData?.for_client.profile_pic_29p}
                                alt={caseData?.for_client.first_name}
                                style={{
                                  width: "19px",
                                  height: "19px",
                                  background: "white",
                                }}
                              />
                              {/* <div className="border-overlay-modal" /> */}
                            </div>
                          ) : (
                            <div
                              style={{
                                width: "19px",
                                height: "19px",
                              }}
                              className="icon-container-modal d-flex align-items-center justify-content-center"
                            >
                              <img
                                src={avatarImage}
                                alt=""
                                style={{
                                  width: "19px",
                                  height: "19px",
                                  background: "var(--primary-30)",
                                }}
                              />
                              {/* <div className="border-overlay-modal" /> */}
                            </div>
                          )}
                        </div>

                        {(caseData?.for_client?.last_name || caseData?.for_client?.first_name) && (
                          <span
                            className="clientTabFont text-black d-block text-nowrap text-decoration-none"
                            style={{ fontSize: "14px", fontWeight: "600" }}
                          >
                            {caseData.for_client.last_name}
                            {caseData.for_client.first_name && (
                              <>
                                <span className="text-dark-grey">,</span>&nbsp;
                              </>
                            )}
                            {caseData.for_client.first_name}
                          </span>
                        )}
                      </div>

                      {/* Row 2 - Phone & Birthday */}
                      {caseData?.for_client?.person_company === "Company" ? (
                        <div
                          className="d-flex align-items-center justify-content-between p-r-5 p-l-5"
                          style={{ height: "21px" }}
                        >
                          <div className="d-flex align-items-center">
                            <span className="ic-avatar ic-15 m-0 d-flex align-items-center m-r-5">
                              <i
                                className="ic ic-client"
                                style={{ width: "15px", height: "15px" }}
                              />
                            </span>
                            <p className="font-weight-semibold m-r-5">
                              {caseData.for_client?.main_contact?.name || ""}
                            </p>
                          </div>
                          <div className="d-flex align-items-center">
                            <span className="ic-avatar ic-15 m-0 d-flex align-items-center m-r-5">
                              <i className="ic ic-15 ic-sms-3d" />
                            </span>
                            <span className="text-nowrap text-black text-decoration-none">
                              {caseData.for_client.main_contact?.phone_number && (
                                <span className="font-weight-semibold">
                                  {formatPhoneNumber(
                                    caseData.for_client.main_contact?.phone_number
                                  )}
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-between p-r-5 p-l-5"
                          style={{ height: "21px" }}
                        >
                          <div className="d-flex align-items-center">
                            <span className="ic-avatar ic-15 m-0 d-flex align-items-center m-r-5">
                              <img
                                src={birthdayIcon}
                                alt=""
                                style={{ width: "15px", height: "15px" }}
                              />
                            </span>
                            <p className="font-weight-semibold m-r-5">
                              {caseData.for_client?.birthday
                                ? formatCalendarDate(caseData.for_client?.birthday)
                                : ""}
                            </p>
                            <div className="d-flex align-items-center">
                              <span className="text-primary-25" style={{ fontWeight: "600" }}>
                                age
                              </span>
                              <span className="text-black m-l-5 font-weight-semibold">
                                {calculateAge(caseData.for_client.birthday)}
                              </span>
                            </div>
                          </div>
                          <div className="d-flex align-items-center">
                            <span className="ic-avatar ic-15 m-0 d-flex align-items-center m-r-5">
                              <i className="ic ic-15 ic-sms-3d" />
                            </span>
                            <span className="text-nowrap text-black text-decoration-none">
                              {caseData.for_client.primary_phone?.phone_number && (
                                <span className="font-weight-semibold">
                                  (
                                  {extractDigits(
                                    caseData.for_client.primary_phone.phone_number
                                  ).slice(0, 3)}
                                  )&nbsp;
                                  {extractDigits(
                                    caseData.for_client.primary_phone.phone_number
                                  ).slice(3, 6)}
                                  -
                                  {extractDigits(
                                    caseData.for_client.primary_phone.phone_number
                                  ).slice(6)}
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Row 3 & 4 - Case Info */}
                      {caseData?.for_client?.person_company === "Company" ? (
                        <div style={{ height: "42px" }}>
                          <div
                            className="d-flex justify-content-between align-items-center w-100 p-r-5 p-l-5"
                            style={{ height: "21px" }}
                          >
                            <div className="d-flex align-items-center">
                              {caseData?.case_type?.casetype_icon && (
                                <span className="ic-avatar ic-15 m-r-5 d-flex align-items-center">
                                  <img
                                    style={{ width: "15px", height: "15px" }}
                                    src={caseData?.case_type?.casetype_icon}
                                    alt="icon"
                                  />
                                </span>
                              )}
                              <p className="font-weight-semibold m-r-5">
                                {caseData.case_type?.case_type_category?.name}:
                              </p>
                              <p className="font-weight-semibold">{caseData.case_type?.name}</p>
                            </div>
                          </div>
                          <div
                            className="d-flex justify-content-between align-items-center w-100 p-r-5 p-l-5"
                            style={{ height: "21px" }}
                          >
                            <div className="d-flex align-items-center">
                              <span className="ic-avatar ic-15 m-r-5 d-flex align-items-center">
                                <img
                                  src={fileNumberIcon}
                                  alt=""
                                  style={{ width: "15px", height: "15px" }}
                                />
                              </span>
                              <p className="font-weight-semibold">
                                {caseData.for_client?.file_number || caseData?.file_number}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ height: "42px" }}>
                          <div
                            className="d-flex justify-content-between align-items-center w-100 p-r-5 p-l-5"
                            style={{ height: "21px" }}
                          >
                            <div className="d-flex align-items-center">
                              {caseData?.case_type?.casetype_icon && (
                                <span className="ic-avatar ic-15 m-r-5 d-flex align-items-center">
                                  <img
                                    style={{ width: "15px", height: "15px" }}
                                    src={caseData?.case_type?.casetype_icon}
                                    alt="icon"
                                  />
                                </span>
                              )}
                              <p className="font-weight-semibold">{caseData.case_type?.name}</p>
                            </div>
                            <div className="d-flex align-items-center">
                              <div className="text-label color-green fw-bold m-r-5">OPEN</div>
                              <p className="font-weight-semibold">
                                {caseData.date_open && formatTimestampDate(caseData.date_open)}
                              </p>
                            </div>
                          </div>
                          <div
                            className="d-flex justify-content-between align-items-center w-100 p-r-5 p-l-5"
                            style={{ height: "21px" }}
                          >
                            <div className="d-flex align-items-center">
                              <span className="ic-avatar ic-15 m-r-5 d-flex align-items-center">
                                <img
                                  src={incidentIcon}
                                  alt=""
                                  style={{ width: "15px", height: "15px" }}
                                />
                              </span>
                              <p className="font-weight-semibold">
                                {caseData.incident_date &&
                                  formatCalendarDate(caseData.incident_date)}
                              </p>
                            </div>
                            <div className="d-flex align-items-center">
                              <div className="text-label m-r-5 color-grey-2 fw-bold">{/*  */}</div>
                              <p className="font-weight-semibold">
                                {/* {formatDate(caseData?.last_accessed_date) || "12/12/2024"} */}
                              </p>
                              <p className="font-weight-semibold">
                                {caseData?.for_client?.languages
                                  ?.map((lang) => lang?.name)
                                  .filter(Boolean)
                                  .join(", ")}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </a>
                    <a
                      href={isCurrentCase ? undefined : caseHref}
                      target={isCurrentCase ? undefined : "_blank"}
                      rel="noopener noreferrer"
                      className="last-access-case-action"
                      aria-label={`Open Case in New Tab: ${clientName}`}
                      aria-disabled={isCurrentCase || undefined}
                      tabIndex={isCurrentCase ? -1 : undefined}
                    >
                      Open Case in New Tab
                    </a>
                  </div>
                );
              })}

            {/* Empty slots to complete the checkerboard */}
            {!isLoading &&
              Array.from({ length: emptySlots }).map((_, index) => {
                const totalIndex = filledSlots + index;
                const rowIndex = Math.floor(totalIndex / columns);
                const colIndex = totalIndex % columns;
                const isEven = (rowIndex + colIndex) % 2 === 0;

                return (
                  <div
                    key={`empty-${index}`}
                    className={`${isEven ? "bg-checkerboard-primary" : "bg-checkerboard-secondary"}`}
                    style={{ height: `${CARD_HEIGHT}px` }}
                  />
                );
              })}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default LastAccessedCasesModal;
